/**
 * Campaigns API
 *
 * Helper functions for managing campaigns: CRUD, responses, and analytics.
 * Relies on core request helpers for auth and error handling.
 */
import { get, post, put, del } from '../core';

/**
 * Helper function to check if user has premium access
 * @returns {boolean} - Whether the user has premium access
 */
export function checkPremiumAccess() {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const isPremium = userData?.planLevel === 'premium' || 
                   userData?.subscriptionStatus?.currentPlanLevel === 'premium';
  return isPremium;
}

/**
 * Fetches all campaigns for the current namespace
 * @param {string} namespaceId - The ID of the namespace
 * @param {Object} options - Additional options for the request
 * @returns {Promise<Array>} - The list of campaigns
 */
export function getCampaigns(namespaceId, options = {}) {
  return get(`/campaign/campaigns?namespaceId=${namespaceId}`, options);
}

/**
 * Fetches a single campaign by ID
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} campaignId - The ID of the campaign
 * @param {Object} options - Additional options for the request
 * @returns {Promise<Object>} - The campaign data
 */
export function getCampaign(namespaceId, campaignId, options = {}) {
  return get(`/campaign/campaigns/${campaignId}?namespaceId=${namespaceId}`, options);
}

/**
 * Creates a new campaign in the specified namespace
 * @param {string} namespaceId - The ID of the namespace
 * @param {Object} campaignData - The campaign data to create
 * @returns {Promise<Object>} - The created campaign
 */
export function createCampaign(namespaceId, campaignData) {
  return post(`/campaign/campaigns?namespaceId=${namespaceId}`, campaignData);
}

/**
 * Updates an existing campaign
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} campaignId - The ID of the campaign to update
 * @param {Object} campaignData - The updated campaign data
 * @returns {Promise<Object>} - The updated campaign
 */
export function updateCampaign(namespaceId, campaignId, campaignData) {
  return put(`/campaign/campaigns/${campaignId}?namespaceId=${namespaceId}`, campaignData);
}

/**
 * Deletes a campaign
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} campaignId - The ID of the campaign to delete
 * @returns {Promise<Object>} - The response
 */
export function deleteCampaign(namespaceId, campaignId) {
  return del(`/campaign/campaigns/${campaignId}?namespaceId=${namespaceId}`);
}

/**
 * Fetches campaign responses
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} campaignId - The ID of the campaign
 * @param {Object} options - Additional options for the request
 * @returns {Promise<Array>} - The list of responses
 */
export function getCampaignResponses(namespaceId, campaignId, options = {}) {
  // Switch to media responses endpoint which returns signed video URLs
  return get(`/survey/videos/${campaignId}`, options);
}

/**
 * Fetches campaign analytics
 * @param {string} namespaceId - The ID of the namespace
 * @param {string} campaignId - The ID of the campaign
 * @param {Object} queryParams - Query parameters for filtering the analytics
 * @returns {Promise<Object>} - The campaign analytics
 */
export function getCampaignAnalytics(namespaceId, campaignId, queryParams = {}) {
  return get(`/campaign/campaigns/${campaignId}/analytics?namespaceId=${namespaceId}`, { params: queryParams });
}

export default {
  getCampaigns,
  getCampaign,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  getCampaignResponses,
  getCampaignAnalytics
}; 